import { motion } from 'framer-motion';
import { Star, ExternalLink } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/utils/translations';

const REVIEWS = [
  {
    name: 'Javier Pardo Cherta',
    initial: 'J',
    date: { es: 'Hace 3 meses', cat: 'Fa 3 mesos', en: '3 months ago', de: 'Vor 3 Monaten' },
    text: '"Muy cercano y profesional, llevo yendo 15 años tanto yo como mi familia y estamos todos muy contentos. MUY recomendable."',
  },
  {
    name: 'Silvia Mariasis',
    initial: 'S',
    date: { es: 'Hace 4 meses', cat: 'Fa 4 mesos', en: '4 months ago', de: 'Vor 4 Monaten' },
    text: '"Llevamos más de 20 años confiando en el Dr. Rainer Brandt y su equipo, la atención es impecable: técnica refinada, trato humano y empático, y precios justos."',
  },
  {
    name: 'Sara Muñoz González',
    initial: 'S',
    date: { es: 'Hace 1 año', cat: 'Fa 1 any', en: '1 year ago', de: 'Vor 1 Jahr' },
    text: '"Recomiendo a la doctora Lafuente a todo el que me pregunta por referencias. Siempre me atiende sin prisas y me explica y asesora sobre los tratamientos."',
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

function GoogleIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

export default function Reviews() {
  const { language } = useLanguage();
  const t = translations[language].reviews;

  return (
    <section id="reviews" className="py-20 md:py-32 bg-[#e3e6e8]" data-testid="reviews-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#5b6770] mb-6">{t.title}</h2>
          <div className="inline-flex items-center gap-3 bg-[#fff6e5] rounded-2xl px-6 py-3" data-testid="google-rating-badge">
            <span className="text-3xl font-bold text-[#5b6770]">4.8</span>
            <div>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-[#ffa600] text-[#ffa600]" />
                ))}
              </div>
              <p className="text-xs text-[#5b6770] mt-0.5">{t.basedOn}</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
          className="grid md:grid-cols-3 gap-6 mb-10"
        >
          {REVIEWS.map((review, i) => (
            <motion.div
              key={i}
              variants={itemVariants}
              className="bg-white rounded-2xl border border-gray-100 p-6 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300 relative"
              data-testid={`review-card-${i}`}
            >
              <div className="absolute top-4 right-4">
                <GoogleIcon />
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ffa600] to-[#ffcc66] flex items-center justify-center text-white font-bold text-sm">
                  {review.initial}
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-[#5b6770]">{review.name}</h4>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, j) => (
                        <Star key={j} className="w-3 h-3 fill-[#ffa600] text-[#ffa600]" />
                      ))}
                    </div>
                    <span className="text-xs text-gray-400">{review.date[language]}</span>
                  </div>
                </div>
              </div>
              <p className="text-[#5b6770] text-sm leading-relaxed italic">{review.text}</p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <a
            href="https://maps.app.goo.gl/DzuxCRdwJZJqCQBP8"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#5b6770] hover:text-[#ffa600] transition-colors text-sm font-medium"
            data-testid="view-all-reviews"
          >
            <GoogleIcon />
            {t.viewAll}
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
